<template>
    <div>
        <label for="accounts_code" class="block text-sm font-medium text-gray-700 mb-2">Account Code</label>
        <div class="relative">
            <em class="absolute h-[38px] w-[38px] text-right z-10 flex items-center justify-end font-bold not-italic text-sm text-gray-400 select-none" :class="{'text-red-800': !form.account.fields.code_proxy.pristine && !form.account.fields.code_proxy.valid}">{{ code_prefix_transformed }}</em>
            <FormField :field="form.account.fields.code_proxy" namespace="accounts" :name="'code'" class="w-full z-0" custom_input_class="pl-[38px]" />
        </div>
        <p :class="`text-sm ${unique_code_class} mt-1`" v-html="unique_code_message"></p>
    </div>
</template>

<script>
import {computed, watch, onMounted, reactive, ref} from 'vue'
import { useAccountStore } from "@sto/account.store"
import { useAuctioneerStore } from '@sto/auctioneer.store'
import { useUserStore } from "@sto/user.store";
import {getConfigurationValue, isValidField} from '@lib/helpers'

export default {
    props: ['auctioneer_id', 'refresh_flag'],
    setup(props, {emit}) {

        const ACCOUNT_STORE = useAccountStore()
        const AUCTIONEER_STORE = useAuctioneerStore()

        const state = reactive({
            code_prefix: null,
            current_suggestion: null,
            requested_code_is_available: false,
            checking_code_timeout: null,
            current_code_is_checked: false,
            code_check_loading: false
        })

        const form = reactive({
            account: {
                loading: false,
                fields: {
                    code_proxy: {
                        active: true,
                        label: '',
                        type: 'text',
                        value: '',
                        placeholder: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            {type: 'characters', value: 'slug'},
                        ],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    code: {
                        active: true,
                        label: '',
                        type: 'hidden',
                        value: '',
                        placeholder: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [
                            {type: 'characters', value: 'slug'},
                        ],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                }
            }
        })

        const code_prefix_transformed = computed(() => {
            if (!state.code_prefix || typeof state.code_prefix !== 'string') return ''
            return state.code_prefix+'-'
        })
        
        const getCodePrefix = async () => {
            // only continue if we actually have an auctioneer to work with
            if (!props.auctioneer_id) return
            const configurations = await AUCTIONEER_STORE.getConfigurations(props.auctioneer_id)
            state.code_prefix = getConfigurationValue(configurations, 'auctioneer_account_code_prefix')
        }

        const getSuggestedAccountCode = async () => {
            // only continue if we actually have an auctioneer to work with
            if (!props.auctioneer_id) return
            let suggested_code = await AUCTIONEER_STORE.getSuggestedAccountCode(props.auctioneer_id)

            let code_parts = suggested_code.split('-');
            code_parts.shift();
            form.account.fields.code_proxy.value = code_parts.join('-');
            state.current_suggestion = suggested_code
            state.current_code_is_checked = true
            form.account.fields.code_proxy.pristine = false
            form.account.fields.code_proxy.valid = true
        }

        const checkCodeAvailability = async () => {

            if (form.account.fields.code_proxy.value.length === 0) return

            if (state.code_check_loading) return

            form.account.fields.code_proxy.pristine = false
            form.account.fields.code_proxy.valid = true
            state.requested_code_is_available = false
            state.code_check_loading = true

            try {
                state.checking_code = true
                // grab the matching accounts
                const existing_accounts = await ACCOUNT_STORE.list([{key: 'page', value: 1}, {key: 'auctioneer_id', value: props.auctioneer_id}, {key: 'code', value: form.account.fields.code.value}])

                // update the message based on the response value
                if (!isNaN(existing_accounts?.pagination?.total) && existing_accounts?.pagination?.total > 0) {
                    state.requested_code_is_available = false
                    form.account.fields.code_proxy.valid = false
                } else {
                    state.requested_code_is_available = true
                    form.account.fields.code_proxy.valid = true
                }

            } catch (error) {
                console.error('BUYER_STORE.listForOrganisation error', error)
                state.requested_code_is_available = false

            } finally {
                state.current_code_is_checked = true
                state.code_check_loading = false
            }

        }

        const code_is_unique = computed(() => {
            return form.account.fields.code_proxy.value.length > 0 && form.account.fields.code.value == state.current_suggestion || state.requested_code_is_available
        })

        const unique_code_message = computed(() => {
            if (!form.account.fields.code_proxy.value.length) {
                return '<strong class="font-semibold">Enter a unique buyer code</strong> (letters, numbers, hyphens and underscores only)'

            } else if (!state.current_code_is_checked) {
                return '<strong class="font-semibold">Checking...</strong>'

            } else if (code_is_unique.value) {
                return `<strong class="font-semibold">${form.account.fields.code.value}</strong> is available`

            } else if (!code_is_unique.value) {
                return `<strong class="font-semibold">${form.account.fields.code.value}</strong> is already in use`

            } else {
                return '<strong class="font-semibold">Enter a unique buyer code</strong> (letters, numbers, hyphens and underscores only)'
            }
        })

        const unique_code_class = computed(() => {

            if (!form.account.fields.code_proxy.value.length) {
                return 'text-gray-700'
            } else if (!state.current_code_is_checked) {
                return 'text-gray-700'
            } else if (code_is_unique.value) {
                return 'text-green-600'
            } else if (!code_is_unique.value) {
                return 'text-red-600'
            } else {
                return 'text-gray-700'
            }
        })

        const auctioneerUpdated = async () => {
            state.code_check_loading = true
            state.current_code_is_checked = false
            await getCodePrefix()
            await getSuggestedAccountCode()
            state.code_check_loading = false
        }
        
        watch (
            () => [
                props.auctioneer_id,
                props.refresh_flag
            ],
            (new_value) => {
                auctioneerUpdated()
            }
        )

        watch(
            () => form.account.fields.code_proxy.value,
            (new_value, old_value) => {
                form.account.fields.code.value = `${code_prefix_transformed.value}${new_value}`
            }
        )

        watch (
            () => form.account.fields.code.value,
            (new_value) => {
                if (state.current_suggestion != new_value) {
                    state.current_code_is_checked = false
                    if (state.checking_code_timeout) clearTimeout(state.checking_code_timeout)
                    state.checking_code_timeout = setTimeout(checkCodeAvailability, 800)
                }

                emit('value_update', new_value)
            }
        )

        onMounted(() => {
            getCodePrefix()
        })

        return {
            form,
            state,
            code_prefix_transformed,
            unique_code_message,
            unique_code_class
        }
    }
}
</script>